import { useTranslation } from 'react-i18next';
import { FlagIcon } from '@/lib/flags';
import { changeLanguage } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import type { LocaleInfo } from '@/types';

interface LoginLanguageSwitcherProps {
	locales: LocaleInfo[];
}

/**
 * Language switcher for the login page.
 * Displays flag buttons for available locales.
 * Only renders when there are 2+ locales available.
 */
export function LoginLanguageSwitcher({ locales }: LoginLanguageSwitcherProps) {
	const { i18n } = useTranslation();
	const currentLang = i18n.language;

	if (locales.length <= 1) {
		return null;
	}

	return (
		<div
			data-ui="login-language-switcher"
			className="flex items-center justify-center gap-2 mt-4"
		>
			{locales.map((locale) => (
				<button
					key={locale.code}
					type="button"
					onClick={() => changeLanguage(locale.code)}
					className={cn(
						'flex items-center justify-center w-10 h-10 rounded-md transition-all',
						'hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
						currentLang === locale.code && 'ring-2 ring-primary bg-primary/5'
					)}
					title={locale.name}
					aria-label={locale.name}
					aria-current={currentLang === locale.code ? 'true' : undefined}
				>
					<FlagIcon code={locale.code} className="h-5 w-7" />
				</button>
			))}
		</div>
	);
}
